import {
  AmiliHealthOrderStatus,
  BiobakeryPipeline,
  ChannelName,
  Currency,
  DonorScreeningType,
  ExternalSampleType,
  KitStatus,
  LANGUAGE,
  ORDER_TYPE,
  OrgType,
  PdfGeneratorVersion,
  ProductType,
  REPORT_STATUS,
  ReportOrderField,
  ReportTabKey,
  Role,
} from '@/enums';

// https://app.shortcut.com/amili-tech/story/1421/project-level-control-for-dna-extraction-status
// Only affects to "Research" and "Amili Analytics" channels
export const projectAllowDnaExtractionChannels = [
  ChannelName.RESEARCH,
  ChannelName.AMILI_ANALYTICS,
];

// https://app.shortcut.com/amili-tech/story/1469/fe-update-new-fields-in-filter-project
// Project settings' Amili PIC select options are users of "Operations" role
export const userOnlyAllowedOperationRole: number = Role.OPERATIONS;

// https://app.clickup.com/t/86ctxmfud
// The Prefix in project settings only applies to the "Research" and "Amili Analytics" channels
export const prefixVisibleChannels = [
  ChannelName.AMILI_ANALYTICS,
  ChannelName.RESEARCH,
];

// https://app.shortcut.com/amili-tech/story/1122/fe-enhance-some-issues-related-to-commercial-flow
// Limit query Project data only applies to the "Amili Health" and "Amili Analytics" channels, using in Commercial Analysis flow
export const performCommercialVisibleChannels: string[] = [
  ChannelName.AMILI_HEALTH,
  ChannelName.AMILI_ANALYTICS,
  ChannelName.CONSUMER_HEALTH,
];

// https://app.shortcut.com/amili-tech/story/1122/fe-enhance-some-issues-related-to-commercial-flow
// Research Analyses only allowed query Projects data with "FASTQ" Sample type
export const researchAnalysisAllowedSampleType = ExternalSampleType.FASTQ;

// https://app.shortcut.com/amili-tech/story/1122/fe-enhance-some-issues-related-to-commercial-flow// Analysis module only allowed query Projects data with "16s" Sequencing Type
export const projectAllowed16sSequencingType = BiobakeryPipeline.SIXTEEN_S;

// https://app.clickup.com/t/86cw59yfu
// Allowed filtering channels that exist "Research" order type
export const channelOrderTypeAllowedResearch = ORDER_TYPE.RESEARCH;

// https://app.clickup.com/t/86cxye4ge
// Orders only allowed query data with "Amili Health" channel when user is in External Organization
export const orderOnlyAllowedAmiliHealthChannel = ChannelName.AMILI_HEALTH;

// https://app.clickup.com/t/86cvg0na3
// Creating project excluded "AMD" channel because it's a Project too
export const channelExcludedForProjectCreation = ChannelName.AMD;

// https://app.clickup.com/t/86cu6ypfz
// Project form values can change logic by channel
export const channelForUpdateLogicProjectFormValues = ChannelName.EXTERNAL;

// https://app.shortcut.com/amili-tech/story/1565/enhance-report-module-reports-ready-column
// Allowed query data by fields for sorting reports
export const reportsAllowedSampleReceivedDateField =
  ReportOrderField.SAMPLE_RECEIVED_DATE;

// https://app.shortcut.com/amili-tech/story/1111/fe-enhance-the-download-report-in-report-module
// Allowed query Ready Reports data by PUBLISHED and UNPUBLISHED statuses
export const reportStatusesConsideredReady = [
  REPORT_STATUS.PUBLISHED,
  REPORT_STATUS.UNPUBLISHED,
];
// https://app.shortcut.com/amili-tech/story/1630/update-pdf-qc-logic-for-bacteria-composition-plot-traffic-light-system
// Kit report status list
export const kitReportStatusList: string[] = [
  KitStatus.REPORT_ERROR,
  KitStatus.REPORT_UNDER_SUPPORT,
  KitStatus.REPORT_READY,
];
// https://app.shortcut.com/amili-tech/story/1630/update-pdf-qc-logic-for-bacteria-composition-plot-traffic-light-system
// Default kit report status when kit not in kit report status list
export const defaultKitReportStatus = KitStatus.REPORT_UNDER_SUPPORT;

// Channels that support creating multiple orders across multiple projects simultaneously
export const multiProjectOrderEnabledChannels = [ChannelName.RESEARCH];

// Channels that require manual entry of kit information.
export const manualOrderRequiredChannels = [
  ChannelName.AMILI_HEALTH,
  ChannelName.CONSUMER_HEALTH,
];

// Business decision: only Amili Health products are invoiced through Xero.
// Product Create/Edit shows the Xero settings (item / account code picker),
// requires a pick and saves the codes ONLY when the product's channel is in
// this list - every other channel has no Xero inputs at all.
export const xeroEnabledProductChannels: string[] = [ChannelName.AMILI_HEALTH];

// https://app.shortcut.com/amili-tech/story/1630/update-pdf-qc-logic-for-bacteria-composition-plot-traffic-light-system
export const reportStatusColor: Record<
  string,
  { color: string; textColor: string }
> = {
  [KitStatus.REPORT_READY]: {
    color: '#EBF9EB',
    textColor: '#00AB00',
  },
  [KitStatus.REPORT_UNDER_SUPPORT]: {
    color: '#FFF3F1',
    textColor: '#FFAC00',
  },
  [KitStatus.REPORT_ERROR]: {
    color: 'var(--light-error)',
    textColor: 'var(--error)',
  },
};

// https://app.shortcut.com/amili-tech/story/1630/update-pdf-qc-logic-for-bacteria-composition-plot-traffic-light-system
export const reportStatusTierColor: Record<string, KitStatus> = {
  GREEN: KitStatus.REPORT_READY,
  YELLOW: KitStatus.REPORT_UNDER_SUPPORT,
  RED: KitStatus.REPORT_ERROR,
};

export const reportTypeSupportBulkUpdateStatus = [
  ReportTabKey.ANALYSIS,
  ReportTabKey.SIXTEEN_S,
];

// UAT 18/11/2025 - by Isha, confirming that projects of channel "Amili Health" will have "Data Governance" section hidden
export const projectChannelWithGovernance = [
  ChannelName.EXTERNAL,
  ChannelName.AMD,
  ChannelName.FMT,
  ChannelName.RESEARCH,
  ChannelName.AMILI_ANALYTICS,
];
//https://amili-workspace.slack.com/archives/C07LXCNMJ3D/p1763384251141959
//  Clinic access: Order status change -
// i. Clinics should only be allowed to update the order status to received - other status changes to processed/delivered/completed are not allowed
export const statusAllowToChangesForClinic = [
  AmiliHealthOrderStatus.RECEIVED,
  AmiliHealthOrderStatus.CANCELLED,
];
// ii.Status Change to "cancelled" should be allowed only if the current order status is "Created"
export const statusesUnableToChangeToCancelled: string[] = [
  AmiliHealthOrderStatus.COMPLETED,
  AmiliHealthOrderStatus.DELIVERED,
  AmiliHealthOrderStatus.RECEIVED,
  AmiliHealthOrderStatus.PROCESSED,
];

export const customErrorCodePrefix = 'AMILI_ERROR_';

export const fmtFlowProductTypes = [ProductType.FMT];

export const kitStatusRequireForShotgunFlowIndicator = KitStatus.KIT_RECEIVED;

export const freeDeliveryCurrencies = [Currency.SGD];

export const defaultLanguageOptions = [LANGUAGE.EN, LANGUAGE.CN];

// Dashboard > Product Operations tab: the "Product" filter's "GHT" option has
// no dedicated ProductType of its own on the backend, so it's matched by a
// name substring instead of an exact productType filter.
export const dashboardGHTProductNameFilter = 'Enhanced Gut Health Test Kit';

// Dashboard > Product Operations tab: "Optimise" and "Probiotics" are both
// backed by the same PROBIOTIC productType — the API has no way to tell them
// apart by type alone, so both send this same productType filter and are
// split further client-side (see dashboardOptimiseProductNameMarker below).
export const dashboardProbioticsProductType = ProductType.PROBIOTIC;

// Dashboard > Product Operations tab, Stock Tracker table: within the
// PROBIOTIC products returned for "Optimise"/"Probiotics", "Optimise" is the
// subset whose name contains this marker; "Probiotics" is everything else.
export const dashboardOptimiseProductNameMarker = 'CGM';

// Dashboard > Product Operations tab, Stock Tracker table: "Probiotics" only
// shows this fixed set of products, in this exact order — not just
// "everything not Optimise" from the PROBIOTIC productType query.
export const dashboardProbioticsStockTrackerAllowlist = [
  'Probiotics - Formulation 1',
  'Probiotics - Formulation 2',
  'Probiotics - Formulation 3',
  'Probiotics - Formulation 4',
  'Restorative Health Formulation',
  'Metabolic Health Formulation',
];

// https://app.shortcut.com/amili-tech/story/8120/logic-specifications-ui-changes-on-dev-dashboard-mod
// Dashboard > Product Operations tab, Stock Tracker table: "Reorder status"
// is derived from `daysOfStockLeft`, with different day-cutoffs for
// GHT/Optimise vs Probiotics (Probiotics has a longer shelf/usage cycle).
// Healthy: > healthyAboveDays. Monitor: (monitorAboveDays, healthyAboveDays].
// Order Now: <= monitorAboveDays. `daysOfStockLeft === null` (no manual stock
// baseline recorded yet) is always "Unavailable", regardless of product.
export const dashboardStockReorderThresholds: Record<
  'GHT' | 'Optimise' | 'Probiotics',
  { healthyAboveDays: number; monitorAboveDays: number }
> = {
  GHT: { healthyAboveDays: 90, monitorAboveDays: 45 },
  Optimise: { healthyAboveDays: 90, monitorAboveDays: 45 },
  Probiotics: { healthyAboveDays: 120, monitorAboveDays: 75 },
};

// Dashboard > Product Operations tab, Product Journey section: the
// "Projects to review" card counts projects (within the current period's
// bucket) whose 16S pipeline setting is either missing (null — no setting
// configured yet) or has a pdfGeneratorVersion outside these — i.e. still
// on an older PDF generator and needing manual review before it's safe to
// migrate them. v7 runs alongside v6, so both count as current.
export const dashboardProjectReviewPdfGeneratorVersions: string[] = [
  PdfGeneratorVersion.V6,
  PdfGeneratorVersion.V7,
];
export const printReportCurrencies = [Currency.SGD];

// Dashboard > Product Operations tab, Business Insights section: "Order
// Value Trend" only sums orders whose project is billed in this currency —
// other currencies would misrepresent the trend since the value isn't
// converted/normalized anywhere.
export const dashboardOrderValueTrendCurrency = Currency.SGD;

// FMT Donor detail > Screening Overview: if AML1 or Nasal Swab is marked
// "Failed", every screening test ordered after it (per the fixed
// `screeningTestOrder` sequence) auto-updates to "NA" and has its status
// control disabled — once one of these two gating tests fails, the
// remaining tests further down the fixed sequence are moot.
export const screeningFailureCascadeTriggerTypes: DonorScreeningType[] = [
  DonorScreeningType.AML1,
  DonorScreeningType.NASAL_SWAB,
];
// Kit module: channel tabs used to be filtered per channel by the user's
// channel-scope permission - that check is no longer needed, so every org
// now sees every channel tab. The one carve-out is
// CLINIC-type orgs, which only ever work through the Amili Health channel:
// for them the channel tab selector is hidden entirely and the Kit list is
// pinned to this channel instead.
export const kitChannelSelectionHiddenForOrgType = OrgType.CLINIC;
export const kitDefaultChannelForHiddenSelection = ChannelName.AMILI_HEALTH;

// Sider > Product menu badge: active products whose stock quantity is at or
// below this number (0 to criticalProductQuantity, inclusive) are counted as
// needing a restock, and the count is shown next to the Product menu item as
// a warning. Sent as `quantityTo` to GET /products/count-by-quantity.
export const criticalProductQuantity = 50;

/**
 * Hardcoded (no backend scope) permission controls that only apply to
 * clinic-type tenants - anything not listed, or any non-clinic org, is left
 * on. Add new entries here instead of hardcoding `orgType === OrgType.CLINIC`
 * checks at call sites. See useCheckInAppClinicSettings for how this is
 * consumed.
 *
 * Nest a boolean as deep as needed to name what it's turning off:
 * `module.component: false` hides a whole component outright, while
 * `module.category.setting: false` turns off one setting within a
 * component/category (e.g. a single filter or column).
 */
export const CLINIC_SETTINGS = {
  project: {
    filter: {
      onboarded: false,
      channel: false,
      amiliPic: false,
    },
    column: {
      onboarded: false,
    },
  },
  kit: {
    filter: {
      orderConfirmedDate: false,
    },
    column: {
      turnaroundTime: false,
    },
  },
  order: {
    filter: {
      amiliPic: false,
    },
    form: {
      // Order Create/Edit (desktop + mobile): clinics don't pick product
      // quantities - every product is added once.
      productQuantity: true,
      // Order lines' expandable component / sub-component breakdown
      // (internal packing cross-check) - clinics don't see the arrow at all.
      productBreakdown: false,
    },
    action: {
      confirm: false,
      edit: false,
      addNote: true,
      setInvoiceNumber: false,
      delete: false,
    },
  },
  report: {
    // Whole component hidden - `module.component: false` directly, no
    // category to traverse (vs. a granular option within a component, e.g.
    // `column.formulation` below, which only turns off one field/filter).
    filter: false,
    column: {
      reportStatus: false,
      channel: false,
      projectName: false,
      formulation: false,
    },
  },
} as const;
